import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/src/supabaseClient';
import { Session, User } from '@supabase/supabase-js';
import { UserProfile } from '@/types';
import { supabaseService } from '@shared/services/supabaseService';
import packageJson from '@/package.json';
import { logger } from '@shared/logging/logger';
import { apiClient } from '@shared/api/client';
import { LoginState, SplashScreen } from '@shared/components/SplashScreen';
import { useRuntimeInspector } from '@shared/monitoring/RuntimeMonitor';

type AuthRole = 'citizen' | 'collector' | 'company' | 'attendant' | 'manager' | 'unknown';

export enum EnterpriseRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  ATTENDANT = 'ATTENDANT',
  CITIZEN = 'CITIZEN'
}

interface ProfileData {
  id: string;
  nome?: string;
  email?: string;
  tipo_perfil?: string;
  photo_url?: string;
  cidade_usuario?: string;
  status_aprovacao?: 'Pendente' | 'Aprovado' | 'Rejeitado';
  senha_temporaria?: boolean;
  termos_aceitos?: number;
  pontos?: number;
  medalha_selecionada?: string | null;
  sequencia?: number;
  ultimo_checkin?: string;
  cellphone?: string;
  [key: string]: any;
}

interface AdminData {
  usuario_id: string;
  setor?: string;
  ecoponto_id?: string;
  [key: string]: any;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: ProfileData | null;
  adminProfile: AdminData | null;
  role: AuthRole;
  userProfileType: UserProfile | null;
  isLoading: boolean;
  loginState: LoginState;
  isDailyCheckInDone: boolean;
  streak: number;
  userPoints: number;
  userExp: number;
  assignedEcopontoName: string;
  assignedEcopontoId: string;
  latestTermsVersion: number;
  showUpdatedTerms: boolean;
  enterpriseRole: EnterpriseRole;
  isPasswordRecovery: boolean;
  clearPasswordRecovery: () => void;
  
  getUserAccessProfile: () => { isAdmin: boolean; isAttendant: boolean; setor: string | null };
  refreshAuth: () => Promise<void>;
  updateUserProfile: (newData: Partial<ProfileData>) => Promise<{ error: any }>;
  logout: () => Promise<void>;
  setShowUpdatedTerms: (show: boolean) => void;
  setIsDailyCheckInDone: (done: boolean) => void;
  setStreak: (val: number) => void;
  setUserPoints: (val: number) => void;
  setUserExp: (val: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useRuntimeInspector('AuthProvider');
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [adminProfile, setAdminProfile] = useState<AdminData | null>(null);
  const [role, setRole] = useState<AuthRole>('unknown');
  const [userProfileType, setUserProfileType] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasInitiallyChecked = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  // States copied from main system to keep sync
  const [isDailyCheckInDone, setIsDailyCheckInDone] = useState(false);
  const [streak, setStreak] = useState(0);
  const [userPoints, setUserPoints] = useState(0);
  const [userExp, setUserExp] = useState(0);
  const [assignedEcopontoName, setAssignedEcopontoName] = useState("");
  const [assignedEcopontoId, setAssignedEcopontoId] = useState("");
  const [latestTermsVersion, setLatestTermsVersion] = useState(0);
  const [showUpdatedTerms, setShowUpdatedTerms] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  const clearPasswordRecovery = useCallback(() => {
    setIsPasswordRecovery(false);
  }, []);

  // Login State Machine States
  const [loginState, setLoginState] = useState<LoginState>(LoginState.BOOT);
  const currentLoginStateRef = useRef<LoginState>(LoginState.BOOT);
  const startTimesRef = useRef<Record<string, number>>({});
  const [showSplash, setShowSplash] = useState(true);

  const transitionTo = useCallback((nextState: LoginState) => {
    const now = Date.now();
    const currentState = currentLoginStateRef.current;
    
    if (currentState) {
      const startTime = startTimesRef.current[currentState];
      if (startTime) {
        const duration = now - startTime;
        logger.info('perf_bootstrap', `Stage [${currentState}] completed in ${duration}ms`);
      }
    }
    
    startTimesRef.current[nextState] = now;
    currentLoginStateRef.current = nextState;
    setLoginState(nextState);
    logger.info('state_machine', `LoginState transition: ${currentState || 'INITIAL'} -> ${nextState}`);
    
    if (nextState === LoginState.READY) {
      setTimeout(() => {
        setShowSplash(false);
      }, 500); // Smooth fade out duration matching framer-motion exit transition
    } else {
      // Only show full screen splash during initial boot/load sequence
      if (!hasInitiallyChecked.current) {
        setShowSplash(true);
      }
    }
  }, []);

  const fetchAuthData = useCallback(async (userId: string) => {
    transitionTo(LoginState.FETCH_PROFILE);
    try {
      // Fetch profile via HTTP API, direct Supabase, admin data, and params in parallel!
      const [apiResult, sbUserResult, compProfileResult, adminResult, paramsResult] = await Promise.allSettled([
        apiClient.get('/profile'),
        supabase
          .from('usuarios')
          .select('*, usuarios_ligas(xp_total)')
          .eq('id', userId)
          .maybeSingle(),
        supabase
          .from('usuario_perfil_complementar')
          .select('*')
          .eq('usuario_id', userId)
          .maybeSingle(),
        supabase
          .from('usuarios_admin')
          .select('*')
          .eq('usuario_id', userId)
          .maybeSingle(),
        supabase
          .from('app_params')
          .select('termos_version')
          .maybeSingle()
      ]);

      let userData: any = null;
      let apiSuccess = false;
      let apiData: any = null;

      const apiResponse = apiResult.status === 'fulfilled' ? apiResult.value : null;
      if (apiResponse && apiResponse.data && apiResponse.data.success) {
        apiData = apiResponse.data.data;
        console.log("[API Success] Profile loaded successfully via GET /api/v1/profile:", apiData);
        userData = {
          id: apiData.id,
          nome: apiData.nome,
          email: apiData.email,
          cpf: apiData.cpf,
          celular: apiData.celular,
          tipo_perfil: apiData.tipo_perfil || apiData.tipoPerfil,
          tipoPerfil: apiData.tipoPerfil || apiData.tipo_perfil,
          photo_url: apiData.photo_url || apiData.fotoPerfil || apiData.avatar_url || apiData.foto_perfil,
          fotoPerfil: apiData.fotoPerfil || apiData.photo_url || apiData.avatar_url || apiData.foto_perfil,
          foto_perfil: apiData.foto_perfil || apiData.photo_url || apiData.avatar_url || apiData.fotoPerfil,
          avatar_url: apiData.avatar_url || apiData.photo_url || apiData.fotoPerfil || apiData.foto_perfil,
          perfil_completo: apiData.perfil_completo ?? apiData.perfilCompleto,
          perfilCompleto: apiData.perfilCompleto ?? apiData.perfil_completo,
          cadastro_completo: apiData.perfil_completo ?? apiData.perfilCompleto ?? apiData.cadastro_completo,
          complementar: apiData.complementar,
        };
        apiSuccess = true;
      }

      const sbUserData = sbUserResult.status === 'fulfilled' && sbUserResult.value.data ? sbUserResult.value.data : null;
      const userError = sbUserResult.status === 'fulfilled' ? sbUserResult.value.error : null;
      if (userError && userError.code !== 'PGRST116') {
        supabaseService.logError(userError, 'AuthContext.fetchAuthData.user');
      }

      const compProfileData = compProfileResult.status === 'fulfilled' && compProfileResult.value.data ? compProfileResult.value.data : null;
      const hasCompProfile = Boolean(compProfileData?.id || apiData?.complementar?.id);
      const isPerfilCompleto = Boolean(
        hasCompProfile ||
        apiData?.perfil_completo === true ||
        apiData?.perfilCompleto === true ||
        sbUserData?.perfil_completo === true ||
        sbUserData?.nivel_cadastro === 'VALIDADO' ||
        sbUserData?.cadastro_completo === true
      );

      // Merge API results over Supabase results if API was successful, fallback to Supabase if not
      if (sbUserData) {
        if (apiSuccess && userData) {
          userData = {
            ...sbUserData,
            ...userData,
            nome: userData.nome || sbUserData.nome || sbUserData.name || '',
            photo_url: userData.photo_url || sbUserData.photo_url || sbUserData.foto_perfil || sbUserData.avatar_url || '',
            avatar_url: userData.avatar_url || sbUserData.avatar_url || sbUserData.photo_url || sbUserData.foto_perfil || '',
            foto_perfil: userData.foto_perfil || sbUserData.foto_perfil || sbUserData.photo_url || sbUserData.avatar_url || '',
            perfil_completo: isPerfilCompleto,
            perfilCompleto: isPerfilCompleto,
            cadastro_completo: isPerfilCompleto,
            has_complementary_profile: hasCompProfile,
            nivel_cadastro: isPerfilCompleto ? 'VALIDADO' : (sbUserData?.nivel_cadastro || 'BASICO'),
            complementar: compProfileData || apiData?.complementar
          };
        } else {
          userData = {
            ...sbUserData,
            nome: sbUserData.nome || sbUserData.name || '',
            photo_url: sbUserData.photo_url || sbUserData.foto_perfil || sbUserData.avatar_url || '',
            avatar_url: sbUserData.avatar_url || sbUserData.photo_url || sbUserData.foto_perfil || '',
            foto_perfil: sbUserData.foto_perfil || sbUserData.photo_url || sbUserData.avatar_url || '',
            perfil_completo: isPerfilCompleto,
            perfilCompleto: isPerfilCompleto,
            cadastro_completo: isPerfilCompleto,
            has_complementary_profile: hasCompProfile,
            nivel_cadastro: isPerfilCompleto ? 'VALIDADO' : (sbUserData?.nivel_cadastro || 'BASICO'),
            complementar: compProfileData || null
          };
        }
      } else if (apiSuccess && userData) {
        // If Supabase failed entirely but API worked, we still have user profile
        userData = {
          ...userData,
          perfil_completo: isPerfilCompleto,
          perfilCompleto: isPerfilCompleto,
          cadastro_completo: isPerfilCompleto,
          has_complementary_profile: hasCompProfile,
          nivel_cadastro: isPerfilCompleto ? 'VALIDADO' : 'BASICO',
          complementar: compProfileData || apiData?.complementar,
          status: true
        };
      }

      // Self-heal: Ensure usuarios table is consistent with validated/complete status
      if (isPerfilCompleto && (!sbUserData?.perfil_completo || sbUserData?.nivel_cadastro !== 'VALIDADO')) {
        supabase.from('usuarios').update({
          perfil_completo: true,
          cadastro_completo: true,
          nivel_cadastro: 'VALIDADO',
          data_perfil_completo: new Date().toISOString()
        }).eq('id', userId).then(() => {}).catch(() => {});
      }

      const adminData = adminResult.status === 'fulfilled' && adminResult.value.data ? adminResult.value.data : null;
      const adminError = adminResult.status === 'fulfilled' ? adminResult.value.error : null;
      if (adminError && adminError.code !== 'PGRST116') {
        supabaseService.logError(adminError, 'AuthContext.fetchAuthData.admin');
      }

      let determinedRole: AuthRole = 'citizen';
      let determinedProfileType: UserProfile = UserProfile.CITIZEN;

      transitionTo(LoginState.VERIFY_PERMISSIONS);

      if (adminData) {
        setAdminProfile(adminData);
        if (adminData.setor === 'Atendente') {
          determinedRole = 'attendant';
          determinedProfileType = UserProfile.ATTENDANT;
          
          if (adminData.ecoponto_id) {
            setAssignedEcopontoId(adminData.ecoponto_id);
            const { data: ecoData } = await supabase
              .from('ecopontos')
              .select('nome')
              .eq('id', adminData.ecoponto_id)
              .maybeSingle();
            if (ecoData) {
              setAssignedEcopontoName(ecoData.nome);
            }
          }
        } else if (/^(administrador|gestor|diretor)/i.test(adminData.setor || '') || /desenvolvedor/i.test(adminData.setor || '')) {
          determinedRole = 'manager';
          determinedProfileType = UserProfile.MANAGER;
        }
      } else {
        setAdminProfile(null);
      }

      if (userData) {
        // Resolve image URL
        if (userData.photo_url && !userData.photo_url.startsWith('http')) {
          try {
            userData.photo_url = await supabaseService.getSignedUrl(userData.photo_url);
          } catch (e) {
            supabaseService.logError(e, 'AuthContext.resolvePhoto');
          }
        }

        setProfile(userData);
        
        // Determine role from standard profile if not admin
        if (!adminData) {
          if (userData.tipo_perfil === UserProfile.COLLECTOR) {
            determinedRole = 'collector';
            determinedProfileType = UserProfile.COLLECTOR;
          } else if (userData.tipo_perfil === UserProfile.COMPANY) {
            determinedRole = 'company';
            determinedProfileType = UserProfile.COMPANY;
          } else {
            determinedRole = 'citizen';
            determinedProfileType = UserProfile.CITIZEN;
          }
        }

        // Points and gamification mapping
        if (userData.pontos !== undefined) setUserPoints(userData.pontos);
        
        // Fetch XP
        supabaseService.getUserLeague(userId).then(leagueData => {
          if (leagueData) {
            setUserExp(leagueData.xp_periodo || 0);
          }
        }).catch(e => console.error("Error fetching periodic XP:", e));

        // Sequence / Checkin handling
        if (userData.ultimo_checkin) {
          const lastCheckin = new Date(userData.ultimo_checkin);
          const today = new Date();
          const lastCheckinDate = new Date(lastCheckin.getFullYear(), lastCheckin.getMonth(), lastCheckin.getDate());
          const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const diffInTime = todayDate.getTime() - lastCheckinDate.getTime();
          const diffInDays = Math.floor(diffInTime / (1000 * 3600 * 24));

          setIsDailyCheckInDone(diffInDays === 0);

          if (diffInDays > 1 && userData.sequencia > 0) {
            setStreak(0);
            supabase.from('usuarios').update({ sequencia: 0 }).eq('id', userId).then().catch(e => console.debug('Streak reset note:', e));
          } else {
            setStreak(userData.sequencia || 0);
          }
        } else {
          setIsDailyCheckInDone(false);
          setStreak(0);
        }

        // Check app tracking updates in background (fire-and-forget, do not block main thread)
        const appVersion = packageJson.version;
        supabase
          .from('usuarios')
          .update({ 
            app_version: appVersion,
            ultimo_acesso: new Date().toISOString()
          })
          .eq('id', userId)
          .then()
          .catch(e => console.debug('App tracking update note:', e));

        // Check terms update
        const paramsData = paramsResult.status === 'fulfilled' && paramsResult.value.data ? paramsResult.value.data : null;
        if (paramsData) {
          setLatestTermsVersion(paramsData.termos_version || 0);
          if ((userData.termos_aceitos || 0) < (paramsData.termos_version || 0)) {
            setShowUpdatedTerms(true);
          }
        }
      } else {
        setProfile(null);
      }

      setRole(determinedRole);
      setUserProfileType(determinedProfileType);
    } catch (err) {
      console.error('Error fetching auth context elements:', err);
    }
  }, [transitionTo]);

  const refreshAuth = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      setSession(currentSession);
      setUser(currentSession?.user || null);
      if (currentSession?.user) {
        await fetchAuthData(currentSession.user.id);
      } else {
        setProfile(null);
        setAdminProfile(null);
        setRole('unknown');
        setUserProfileType(null);
      }
    } catch (err) {
      console.error('Error in refreshAuth:', err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchAuthData]);

  const updateUserProfile = useCallback(async (newData: Partial<ProfileData>) => {
    if (!user) return { error: new Error('User not authenticated') };
    const { error } = await supabase
      .from('usuarios')
      .update(newData)
      .eq('id', user.id);
    
    if (!error) {
      await fetchAuthData(user.id);
    }
    return { error };
  }, [user, fetchAuthData]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      setProfile(null);
      setAdminProfile(null);
      setRole('unknown');
      setUserProfileType(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Safety timeout to prevent infinite loading/verifying session state
  useEffect(() => {
    if (!isLoading) return;
    
    const timer = setTimeout(() => {
      if (isLoading) {
        console.warn('[AuthContext] Session loading taking too long (over 4s). Force letting app proceed...');
        setIsLoading(false);
        transitionTo(LoginState.READY);
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [isLoading, transitionTo]);

  useEffect(() => {
    let isMounted = true;
    let isFetching = false;

    const handleAuthChange = async (currentSession: Session | null, origin: string) => {
      if (!isMounted) return;
      logger.debug('auth_context', `handleAuthChange executing (origin: ${origin}) for user: ${currentSession?.user?.id || 'none'}`);
      
      const prevUserId = currentUserIdRef.current;
      currentUserIdRef.current = currentSession?.user?.id || null;
      
      setSession(currentSession);
      setUser(currentSession?.user || null);

      // Store JWT token to enable authentication in apiClient
      if (currentSession?.access_token) {
        localStorage.setItem('wastemap_auth_token', currentSession.access_token);
      } else {
        localStorage.removeItem('wastemap_auth_token');
      }

      if (currentSession?.user) {
        isFetching = true;
        
        // Only trigger isLoading(true) if:
        // 1. Initial boot check hasn't finished yet
        // 2. Or the logged-in user changed completely (e.g. login/out/switch)
        const userChanged = !prevUserId || prevUserId !== currentSession.user.id;
        const needsLoader = !hasInitiallyChecked.current || userChanged;
        
        if (needsLoader) {
          setIsLoading(true);
        }
        try {
          logger.debug('auth_context', `handleAuthChange starting fetchAuthData for user: ${currentSession.user.id}`);
          await fetchAuthData(currentSession.user.id);
          logger.debug('auth_context', `handleAuthChange fetchAuthData completed for user: ${currentSession.user.id}`);
        } catch (error) {
          console.error('[AuthContext] handleAuthChange error during fetchAuthData:', error);
          transitionTo(LoginState.ERROR);
        } finally {
          isFetching = false;
          hasInitiallyChecked.current = true;
          if (isMounted) {
            setIsLoading(false);
            transitionTo(LoginState.READY);
            logger.debug('auth_context', 'handleAuthChange: profile fetch completed, isLoading set to FALSE');
          }
        }
      } else {
        logger.debug('auth_context', 'handleAuthChange: no user session detected');
        setProfile(null);
        setAdminProfile(null);
        setRole('unknown');
        setUserProfileType(null);
        hasInitiallyChecked.current = true;
        if (isMounted) {
          setIsLoading(false);
          transitionTo(LoginState.READY);
          logger.debug('auth_context', 'handleAuthChange: no session, isLoading set to FALSE');
        }
      }
    };

    // Check for password recovery parameters in URL query or hash
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash);
      const tokenHash = urlParams.get('token_hash') || hashParams.get('token_hash');
      const authType = urlParams.get('type') || hashParams.get('type');

      if (tokenHash && authType === 'recovery') {
        logger.info('auth_context', 'Found recovery token_hash in URL, verifying OTP...');
        supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' })
          .then(({ data, error }) => {
            if (error) {
              console.error('[AuthContext] Error verifying recovery token_hash:', error);
            } else if (data.session) {
              setIsPasswordRecovery(true);
              handleAuthChange(data.session, 'OTP_RECOVERY');
            }
            // Clean token from URL history to avoid repeat processing
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          })
          .catch(err => {
            console.error('[AuthContext] Unexpected error verifying recovery token:', err);
          });
      }
    } catch (e) {
      console.debug('[AuthContext] Notice checking URL recovery params:', e);
    }

    // Check initial session
    logger.debug('auth_context', 'Mounting AuthProvider, checking initial session...');
    transitionTo(LoginState.RESTORE_SESSION);
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      if (!isMounted) return;
      logger.debug('auth_context', `Initial getSession completed. User present: ${!!currentSession?.user}`);
      if (!isFetching) {
        handleAuthChange(currentSession, 'getSession');
      } else {
        logger.debug('auth_context', 'Skipping initial getSession load because onAuthStateChange is already fetching');
      }
    }).catch(err => {
      console.error('[AuthContext] Error getting initial session:', err);
      if (isMounted) {
        setIsLoading(false);
        transitionTo(LoginState.ERROR);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      logger.debug('auth_context', `onAuthStateChange event triggered: ${event}. User present: ${!!currentSession?.user}`);
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
        handleAuthChange(currentSession, 'PASSWORD_RECOVERY');
      } else if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        handleAuthChange(currentSession, `onAuthStateChange(${event})`);
      } else {
        // Safe fallback for other actions (INITIAL_SESSION, etc.)
        if (!isFetching && !currentSession?.user) {
          if (isMounted) {
            setIsLoading(false);
            transitionTo(LoginState.READY);
            logger.debug('auth_context', `onAuthStateChange event ${event} without user session completed logic`);
          }
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchAuthData]);

  const getUserAccessProfile = useCallback(() => {
    // Cargo vem só de usuarios_admin (antes: e-mails fixos davam admin sem cargo cadastrado)
    if (!adminProfile) {
      return {
        isAdmin: false,
        isAttendant: false,
        setor: null
      };
    }

    const sectorLower = (adminProfile.setor || '').toLowerCase().trim();
    
    // Para determinar se o usuário é administrador, a coluna setor deve possuir o texto Administrador
    const isAdmin = sectorLower.includes('administrador') ||
                    sectorLower.includes('gestor') ||
                    sectorLower.includes('diretor') ||
                    sectorLower.includes('desenvolvedor');
                    
    // Para determinar se o usuário é atendente, a coluna setor deve possuir o texto Atendente
    const isAttendant = sectorLower.includes('atendente') || 
                        sectorLower === 'atendimento' || 
                        sectorLower.startsWith('atend');

    return {
      isAdmin,
      isAttendant: isAttendant && !isAdmin,
      setor: adminProfile.setor || null
    };
  }, [adminProfile, user, session]);

  const enterpriseRole = useMemo<EnterpriseRole>(() => {
    if (!profile) return EnterpriseRole.CITIZEN;
    
    // Super admin = desenvolvedor em usuarios_admin. Antes: qualquer e-mail começando com
    // "bruno.miranda@wastebank" (sem domínio fixo) — e o e-mail vem da tabela usuarios, editável.
    if (/desenvolvedor/i.test(adminProfile?.setor || '') || (adminProfile as any)?.desenvolvedor === true) {
      return EnterpriseRole.SUPER_ADMIN;
    }
    
    if (role === 'manager') {
      const { isAdmin } = getUserAccessProfile();
      if (isAdmin) {
        return EnterpriseRole.ADMIN;
      }
      return EnterpriseRole.MANAGER;
    }
    
    if (role === 'attendant') {
      return EnterpriseRole.ATTENDANT;
    }
    
    return EnterpriseRole.CITIZEN;
  }, [profile, role, getUserAccessProfile, adminProfile]);

  const contextValue = useMemo(() => ({
    session,
    user,
    profile,
    adminProfile,
    role,
    userProfileType,
    isLoading,
    loginState,
    isDailyCheckInDone,
    streak,
    userPoints,
    userExp,
    assignedEcopontoName,
    assignedEcopontoId,
    latestTermsVersion,
    showUpdatedTerms,
    enterpriseRole,
    isPasswordRecovery,
    clearPasswordRecovery,
    getUserAccessProfile,
    refreshAuth,
    updateUserProfile,
    logout,
    setShowUpdatedTerms,
    setIsDailyCheckInDone,
    setStreak,
    setUserPoints,
    setUserExp
  }), [
    session,
    user,
    profile,
    adminProfile,
    role,
    userProfileType,
    isLoading,
    loginState,
    isDailyCheckInDone,
    streak,
    userPoints,
    userExp,
    assignedEcopontoName,
    assignedEcopontoId,
    latestTermsVersion,
    showUpdatedTerms,
    enterpriseRole,
    isPasswordRecovery,
    clearPasswordRecovery,
    getUserAccessProfile,
    refreshAuth,
    updateUserProfile,
    logout
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <SplashScreen 
        loginState={loginState} 
        isVisible={showSplash} 
        isDarkMode={role === 'manager' || (typeof window !== 'undefined' && document.documentElement.classList.contains('dark'))} 
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
